import { groupBy, orderBy } from "lodash";

/**
 * Groups tags by their type and creates tabs data
 * @param {Array} tags - Array of tag data objects
 * @param {Array} tagTypes - Array of tag type objects
 * @returns {Array} Array of tab objects with id, name, count, and tags
 */
export const createTabsData = (tags, tagTypes) => {
  if (!tagTypes || tagTypes.length === 0) {
    return [];
  }

  // If no tags, create tabs based on tagTypes with empty tags
  if (!tags || tags.length === 0) {
    return tagTypes.map((tagType) => ({
      id: tagType.id,
      name: tagType.name,
      count: 0,
      tags: [],
    }));
  }

  // Group tags by their type
  const groupedTags = groupBy(tags, (tag) => tag?.type);

  // Create tabs data with type info and counts
  return orderBy(
    Object.entries(groupedTags).map(([typeId, typeTags]) => {
      const tagType = tagTypes.find((tt) => tt.id === typeId);
      return {
        id: typeId,
        name: tagType?.name || typeId,
        count: typeTags.length,
        tags: typeTags,
      };
    }),
    "name"
  );
};

/**
 * Filters tags based on selected tab type
 * @param {Array} tabsData - Array of tab objects
 * @param {string} selectedTabType - ID of the selected tab type
 * @returns {Array} Filtered array of tags for the selected tab
 */
export const getFilteredTags = (tabsData, selectedTabType) => {
  if (!selectedTabType) {
    return [];
  }
  const selectedTab = tabsData.find((tab) => tab.id === selectedTabType);
  return selectedTab?.tags || [];
};

const convertTagToTreeNode = (
  entityTag,
  toggledNodes = {},
  isChild = false
) => {
  // Handle both EntityTag format (with nested tag) and direct Tag format
  const tag = entityTag?.tag || entityTag;
  const childCount = tag?.childTags?.total || 0;
  const childTags = tag?.childTags?.tags || [];

  // Only convert child tags if they have been loaded (tags array exists and has items)
  // If tags haven't been loaded yet but total > 0, children will be empty until loaded
  const childNodes =
    childTags.length > 0
      ? childTags.map((childTag) =>
          convertTagToTreeNode(childTag, toggledNodes, true)
        )
      : [];

  return {
    id: tag.id,
    name: tag.displayValue || tag.name || "Unknown Tag",
    toggled: toggledNodes[tag.id] || false,
    active: false,
    children: childNodes,
    disabled: childCount === 0,
    tag: entityTag,
    isChild,
  };
};

/**
 * Transforms tags data into TreeView format
 * @param {Array} tags - Array of tag data objects
 * @param {Object} toggledNodes - Object tracking which nodes are expanded
 * @returns {Array} Array of tree node objects for TreeView
 */
export const transformTagsToTreeData = (tags, toggledNodes = {}) => {
  if (!tags || tags.length === 0) {
    return [];
  }
  return tags.map((tag) => convertTagToTreeNode(tag, toggledNodes));
};
